import React from 'react'
import { useUpdateLog } from 'utils/log'
import TextAreaLine from '../items/TextAreaLine'
import { FormTitleProps } from '../items/FormTitle'
import RadioLine from '../items/RadioLine'
import BaseGroup from './BaseGroup'
import { OptionItem } from '../common'

/**
 * RadioButtonGroupのタイプ定義
 */
export type RadioButtonGroupProps<T> = FormTitleProps & {
  /** ラジオボタンの選択肢一覧 */
  options: Array<string | OptionItem<T>>
  /** 選択中のインデックス */
  selectedIndex?: number
  /** 子要素に値を割り振っている場合に選択中の値を指定 */
  selectedValue?: T
  /** フリー入力の要・不要フラグ */
  needFreeText?: boolean
  /** フリー入力のテキスト */
  freeText?: string
  /** フリー入力の最大文字数 */
  freeMaxLength?: number
  /** 選択変更時のコールバック */
  onChanged?: (index: number) => void
  /** 選択肢に値が設定されている場合の変更時コールバック */
  onValueChanged?: (value: T) => void
  /** 入力テキスト変更時のコールバック */
  onTextChanged?: (text: string) => void
}

/**
 * ラジオグループ
 */
const RadioButtonGroup: <T>(
  props: RadioButtonGroupProps<T>
) => React.ReactElement = <T,>(props: RadioButtonGroupProps<T>) => {
  const {
    title,
    description,
    isRequired,
    hideTitle,
    options,
    selectedIndex,
    selectedValue,
    needFreeText,
    freeText,
    freeMaxLength,
    onChanged,
    onValueChanged,
    onTextChanged,
  } = props

  const [inputText, setInputText] = React.useState(freeText ?? '')

  /** ラジオボタンクリック時のコールバック */
  const handleRadioClick = React.useCallback(
    (index: number, value?: T): void => {
      if (onChanged && selectedIndex != index) {
        onChanged(index)
      }
      if (onValueChanged && value && selectedValue != value) {
        onValueChanged(value)
      }
    },
    [selectedIndex, onChanged, onValueChanged]
  )

  const handleTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
      if (onTextChanged) {
        const newText = event.target.value
        // 内部ステートを更新
        setInputText(newText)
        if (freeMaxLength && newText.length > freeMaxLength) {
          // 最大文字数の指定があり、入力テキストが文字数制限を超えている場合は変更通知を送らない
          return
        }
        onTextChanged(newText)
      }
    },
    [freeMaxLength, onTextChanged]
  )

  useUpdateLog(
    title,
    {
      isRequired: isRequired,
      options: options,
      selectedIndex: selectedIndex,
      selectedValue: selectedValue,
      needFreeText: needFreeText,
      freeText: freeText,
    },
    [props]
  )

  // 子要素生成
  const items: () => React.ReactElement = () => (
    <>
      {options.map((item, index) => {
        const [label, value] =
          typeof item === 'string'
            ? [item, undefined]
            : [item.label, item.value as T]
        return (
          <RadioLine
            key={index}
            label={label}
            isSelected={
              selectedValue ? value === selectedValue : index === selectedIndex
            }
            onClick={(): void => handleRadioClick(index, value)}
          />
        )
      })}
      {needFreeText && (
        <TextAreaLine
          inputText={inputText}
          maxLength={freeMaxLength}
          onChange={handleTextChange}
        />
      )}
    </>
  )

  return (
    <>
      {hideTitle ? (
        items()
      ) : (
        <BaseGroup
          isRequired={isRequired}
          title={title}
          description={description}
        >
          {items()}
        </BaseGroup>
      )}
    </>
  )
}

export default RadioButtonGroup
